// FARBLD plan (pure: no THREE, no fetch — scripts/check-focus.mjs runs it in
// bare node). The far-building LOD is buildings_far.bin (pipeline/farbld.py,
// FBL1): every building of the bake as one closed far shape (its own roof
// triangles + the roof outline dropped to its base, coloured from its own
// GLB), per 2048 m chunk, each chunk's triangles grouped in one contiguous
// RUN per buildings GLB tile. client/src/farbld.js draws it; this file decides
// WHAT it draws, and the budget gate replays the same decision:
//   - only while the focus wedge is in force (the optic band, a raised VR
//     optic, or the desktop / phone view armed past view_arm.zoom_on), on the
//     focus layer: the naked-eye pass never sees it;
//   - a chunk the wedge reaches, to the frame edge (no range);
//   - minus every run whose GLB tile is DRAWN (installed on any layer): the
//     real building is there, so its far shape is not — no double draw, no
//     z-fight; a tile still loading keeps its far shape until it lands;
//   - nearest chunk to the apex first, admitted while the running tris stay
//     within far_buildings.tri_budget and the draws within draw_budget (one
//     draw per chunk).

export const FBL_MAGIC = 0x314c4246;      // 'FBL1' read as a little-endian u32
export const FBL_VERSION = 1;
const HEADER_BYTES = 32, CHUNK_BYTES = 28, SUB_BYTES = 16;

// buildings_far.bin -> { version, chunkM, q, colScale, tris, chunks, pos, col }
// chunks[i] = { cx, cz, chunkM, yBase, tri0, ntris, subs: [{ tx, ty, key, tri0, ntris }] }
// (sub tri0 relative to its chunk, contiguous from 0); pos Int16Array (9 per tri), col
// Uint16Array (3 per tri). Throws on anything but an exact FBL1 file (size,
// and every chunk / sub range inside it).
export function parseFar(buf) {
  const ab = buf instanceof ArrayBuffer ? buf : buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength);
  const dv = new DataView(ab);
  if (ab.byteLength < HEADER_BYTES || dv.getUint32(0, true) !== FBL_MAGIC) throw new Error('farbld: not an FBL1 file');
  const version = dv.getUint32(4, true);
  if (version !== FBL_VERSION) throw new Error(`farbld: FBL1 version ${version}, reader ${FBL_VERSION}`);
  const chunkM = dv.getUint32(8, true), q = dv.getFloat32(12, true), colScale = dv.getFloat32(16, true);
  const nch = dv.getUint32(20, true), tris = dv.getUint32(24, true), nsub = dv.getUint32(28, true);
  let o = HEADER_BYTES;
  const chunks = [];
  const raw = [];
  for (let i = 0; i < nch; i++, o += CHUNK_BYTES) {
    raw.push([dv.getInt32(o, true), dv.getInt32(o + 4, true), dv.getInt32(o + 8, true), dv.getUint32(o + 12, true),
      dv.getUint32(o + 16, true), dv.getUint32(o + 20, true), dv.getUint32(o + 24, true)]);
  }
  const subs = [];
  for (let i = 0; i < nsub; i++, o += SUB_BYTES) {
    const tx = dv.getInt32(o, true), ty = dv.getInt32(o + 4, true);
    subs.push({ tx, ty, key: tx + '_' + ty, tri0: dv.getUint32(o + 8, true), ntris: dv.getUint32(o + 12, true) });
  }
  const end = o + tris * 18 + tris * 6;
  if (end !== ab.byteLength) throw new Error(`farbld: FBL1 size ${ab.byteLength}, layout ${end}`);
  // every range inside the file (a draw never reads past its chunk / the
  // triangle arrays): chunk tri0 + ntris <= tris, its subs inside the sub
  // table, contiguous from 0 and summing to the chunk's ntris
  for (const [cx, cz, yBase, tri0, ntris, sub0, ns] of raw) {
    if (tri0 + ntris > tris) throw new Error(`farbld: chunk ${cx}_${cz} tris ${tri0}+${ntris} past ${tris}`);
    if (sub0 + ns > nsub) throw new Error(`farbld: chunk ${cx}_${cz} subs ${sub0}+${ns} past ${nsub}`);
    const cs = subs.slice(sub0, sub0 + ns);
    let n = 0;
    for (const s of cs) {
      if (s.tri0 !== n) throw new Error(`farbld: chunk ${cx}_${cz} sub ${s.key} at ${s.tri0}, want ${n}`);
      n += s.ntris;
    }
    if (n !== ntris) throw new Error(`farbld: chunk ${cx}_${cz} subs sum ${n}, chunk ${ntris}`);
    chunks.push({ cx, cz, chunkM, yBase, tri0, ntris, subs: cs });
  }
  return {
    version, chunkM, q, colScale, tris, chunks,
    pos: new Int16Array(ab, o, tris * 9),
    col: new Uint16Array(ab, o + tris * 18, tris * 3),
  };
}

// The visible runs of one chunk: its subs minus the drawn GLB tiles, adjacent
// runs merged. -> { runs: [[tri0, n], …], tris }
export function chunkRuns(chunk, drawn) {
  const runs = [];
  let tris = 0;
  for (const s of chunk.subs) {
    if (!s.ntris || (drawn && drawn.has(s.key))) continue;
    const last = runs[runs.length - 1];
    if (last && last[0] + last[1] === s.tri0) last[1] += s.ntris;
    else runs.push([s.tri0, s.ntris]);
    tris += s.ntris;
  }
  return { runs, tris };
}

// THE decision (module comment). focus: a Focus (active, ox, oz, hit());
// drawn: Set of 'tx_ty' GLB tiles drawn now; cfg: focus.tiers[t].far_buildings
// ({ tri_budget, draw_budget }) or null (= off). -> { list: [{ chunk, runs,
// tris, d }], tris, draws, want, dropped } — want = chunks the wedge reaches
// with something left to draw, dropped = those the budget kept out.
export function farPlan(chunks, focus, drawn, cfg) {
  const out = { list: [], tris: 0, draws: 0, want: 0, dropped: 0 };
  if (!cfg || !focus || !focus.active || !chunks || !chunks.length) return out;
  const cand = [];
  for (const c of chunks) {
    const m = c.chunkM || 2048;
    const cx = (c.cx + 0.5) * m, cz = (c.cz + 0.5) * m;
    if (!focus.hit(cx, cz, m * Math.SQRT1_2, Infinity)) continue;
    const r = chunkRuns(c, drawn);
    if (!r.tris) continue;
    cand.push({ chunk: c, runs: r.runs, tris: r.tris, d: Math.hypot(cx - focus.ox, cz - focus.oz) });
  }
  cand.sort((a, b) => a.d - b.d);
  out.want = cand.length;
  for (const e of cand) {
    if (out.tris + e.tris > cfg.tri_budget || out.draws + 1 > cfg.draw_budget) { out.dropped++; continue; }
    out.tris += e.tris;
    out.draws += 1;
    out.list.push(e);
  }
  return out;
}

// The GLB focus want-set (buildings.js BuildingManager.update, moved here so
// the gate replays the client's own rule): tiles the ring will not keep
// (edge distance >= cull + 200) that the wedge hits within range_m, nearest
// first while the summed per-tile `tris` fit tri_budget. dist[i] = the edge
// distance of info[i] from pos (buildings.js computes it once for both).
export function glbFocusWant(info, tileM, dist, focus, fcfg, cull) {
  const want = new Set();
  if (!fcfg) return want;
  const half = tileM * 0.71;
  const cand = [];
  for (let i = 0; i < info.length; i++) {
    if (dist[i] < cull + 200) continue;                 // the ring holds these
    const [tx, ty] = info[i].tile;
    if (focus.hit((tx + 0.5) * tileM, (ty + 0.5) * tileM, half, fcfg.range_m)) cand.push(i);
  }
  cand.sort((a, b) => dist[a] - dist[b]);
  let tris = 0;
  for (const i of cand) {
    // `tris` is per-tile in buildings_index.json; an index old enough to
    // lack it spends the whole budget on one tile rather than none of it
    const c = info[i].tris || fcfg.tri_budget;
    if (tris + c > fcfg.tri_budget) break;
    tris += c;
    want.add(info[i].tile[0] + '_' + info[i].tile[1]);
  }
  return want;
}
